import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import jsQR from 'jsqr';

/**
 * Lector de códigos QR con la cámara trasera (web, iPhone y Android). Llama a `onResult`
 * con el texto del primer código leído y se cierra.
 */
export function QrScanner({ onResult, onClose }: { onResult: (text: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const scan = () => {
      const v = video.current;
      if (!v || done.current || !ctx) return;
      if (v.readyState >= 2 && v.videoWidth) {
        // Se analiza una versión reducida: más rápido en teléfonos modestos.
        const scale = Math.min(1, 640 / v.videoWidth);
        canvas.width = Math.round(v.videoWidth * scale);
        canvas.height = Math.round(v.videoHeight * scale);
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (code?.data) {
          done.current = true;
          onResult(code.data);
          return;
        }
      }
      raf = requestAnimationFrame(scan);
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        raf = requestAnimationFrame(scan);
      } catch {
        setError('No se pudo abrir la cámara. Permite el acceso a la cámara o escribe el código del conductor.');
      }
    })();

    return () => {
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onResult]);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Escanear código QR" className="fixed inset-0 z-[3600] bg-black flex flex-col">
      <video ref={video} className="absolute inset-0 h-full w-full object-cover" playsInline muted />
      <div className="relative flex-1 flex items-center justify-center pointer-events-none">
        <div className="size-64 rounded-3xl border-4 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
      </div>
      <div className="relative p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] text-center text-white">
        <p className="font-semibold">{error ?? 'Apunta al código QR de tu conductor'}</p>
      </div>
      <button className="tap absolute right-4 top-[max(env(safe-area-inset-top),1rem)] size-11 rounded-full bg-black/60 text-white flex items-center justify-center" onClick={onClose} aria-label="Cerrar">
        <X className="size-6" />
      </button>
    </div>,
    document.body,
  );
}
